import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { MessageBar, MessageBarBody, Spinner } from "@fluentui/react-components";
import { oktaAuth } from "../auth/oktaConfig";

export function AuthCallbackRoute() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // A redirect callback consumes the one-time PKCE transaction and can only be
  // handled once. Guard against effect re-runs (navigate identity changes) and a
  // concurrent handler in OktaProvider.start(), which otherwise throws
  // "Could not load PKCE codeVerifier from storage".
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    let cancelled = false;
    const goHome = () => {
      if (!cancelled) void navigate({ to: "/", replace: true });
    };

    void (async () => {
      try {
        if (oktaAuth.isLoginRedirect()) {
          await oktaAuth.handleLoginRedirect();
        }
        goHome();
      } catch (err) {
        // If the transaction was already consumed elsewhere (e.g. start()
        // handled it first), the sign-in likely still succeeded — proceed
        // instead of surfacing a scary error to the user.
        const authed = await oktaAuth.isAuthenticated().catch(() => false);
        if (authed || !oktaAuth.isLoginRedirect()) {
          goHome();
          return;
        }
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (error) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>Erreur d'authentification: {error}</MessageBarBody>
      </MessageBar>
    );
  }

  return <Spinner label="Finalisation de la connexion..." />;
}
